import { Tooltip as ConditionalTooltip } from '@fountain/fountain-ui-components';
import { ChatAltOutlineIcon, Icon } from '@fountain/ripple/icon';
import {
  Divider,
  List,
  ListItem,
  ListSubheader,
  Typography,
} from '@material-ui/core';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import { PHONE_PLATFORM_TYPES } from 'components/SmsMessage/constants';
import { makeSelectExternalId } from 'containers/Auth_old/selectors';
import WhatsappIcon from 'images/whatsapp-logo.svg';

import { MessageTemplatesVariant } from './constants';
import messages from './messages';
import useStyles from './styles';
import {
  getRecentlyUsedTemplates,
  updateRecentlyUsedTemplates,
} from './utils';

const RecentlyUsedTemplatesList = ({
  onSelected,
  variant,
  templateType,
  whatsAppProvider,
  currentFetchedTemplates = [],
}) => {
  const styles = useStyles();
  const userId = useSelector(makeSelectExternalId());
  const currentFetchedIds = useMemo(
    () => new Set(currentFetchedTemplates.map(t => t.id)),
    [currentFetchedTemplates],
  );
  const context = {
    userId,
    variant,
    templateType,
    whatsAppProvider,
    currentFetchedIds,
  };
  const [recentlyUsedTemplates, setRecentlyUsedTemplates] = useState([]);
  useEffect(() => {
    setRecentlyUsedTemplates(getRecentlyUsedTemplates(context));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, variant, templateType, whatsAppProvider, currentFetchedIds]);

  const handleClickListItem = template => () => {
    updateRecentlyUsedTemplates(template, context);
    onSelected(template);
  };

  if (
    !recentlyUsedTemplates ||
    (recentlyUsedTemplates && recentlyUsedTemplates.length === 0)
  ) {
    return null;
  }

  return (
    <>
      {recentlyUsedTemplates && recentlyUsedTemplates.length > 0 && (
        <>
          <List disablePadding>
            <ListSubheader className={styles.listSubheader} disableSticky>
              <Typography variant="h5">
                <FormattedMessage {...messages.recentlyUsedTemplates} />
              </Typography>
            </ListSubheader>
            {recentlyUsedTemplates.map(template => (
              <ListItem
                button
                className={styles.listItem}
                key={template.id}
                onClick={handleClickListItem(template)}
              >
                <ConditionalTooltip
                  dense
                  enterDelay={333}
                  text={template.name}
                  title={template.name}
                >
                  {[
                    MessageTemplatesVariant.SMS,
                    MessageTemplatesVariant.WHATSAPP,
                  ].includes(variant) &&
                    (template.sms_enabled ? (
                      <Icon
                        icon={ChatAltOutlineIcon}
                        size="sm"
                        color="brand"
                        role="img"
                        aria-label={PHONE_PLATFORM_TYPES.sms}
                        className={styles.smsIcon}
                      />
                    ) : (
                      <img
                        src={WhatsappIcon}
                        alt={MessageTemplatesVariant.whats_app}
                        className={styles.templateIcon}
                      />
                    ))}
                  <Typography noWrap variant="caption">
                    {template.name}
                  </Typography>
                </ConditionalTooltip>
              </ListItem>
            ))}
          </List>
          <Divider className={styles.divider} />
        </>
      )}
    </>
  );
};

RecentlyUsedTemplatesList.propTypes = {
  onSelected: PropTypes.func.isRequired,
  variant: PropTypes.oneOf([
    MessageTemplatesVariant.EMAIL,
    MessageTemplatesVariant.SMS,
    MessageTemplatesVariant.WHATSAPP,
  ]).isRequired,
  templateType: PropTypes.string,
  whatsAppProvider: PropTypes.string,
  currentFetchedTemplates: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    }),
  ),
};

export default RecentlyUsedTemplatesList;
